import {Injectable} from '@angular/core';
import {CapacitorHttp, HttpOptions, HttpResponse} from '@capacitor/core';
import {PlatformService} from '../core/platform.service';
import {ThermomixStorageService} from '../storage/thermomix-storage.service';
import {
  ThermomixCredentials,
  ThermomixIngredientJson,
  ThermomixItem,
  ThermomixLocalization,
  ThermomixShoppingListJson,
  ThermomixShoppingRecipeJson,
  ThermomixToken
} from '../../types/thermomix';

/** Das PKCE-Paar aus Challenge und dem Verfahren, mit dem sie gebildet wurde. */
interface ThermomixPkce {
  Challenge: string;
  Method: 'S256' | 'plain';
}

/**
 * Direktzugriff auf Cookidoo (Thermomix) vom Geraet aus.
 *
 * Der Login ist ein OAuth2 Authorization-Code-Flow mit PKCE gegen den CIAM-Identity-Provider
 * von Vorwerk, wie ihn auch die Thermomix-App nutzt:
 *
 *   1. GET  /authz-srv/authz  -> Redirect auf die Login-Seite, dessen Location die requestId traegt
 *   2. POST /login-srv/login  -> Redirect-Kette, die mit der App-URL + ?code=... endet
 *   3. POST /token-srv/token  -> tauscht den Code gegen Access- und Refresh-Token
 *
 * Alle Anfragen laufen ueber CapacitorHttp, also nativ am Geraet vorbei an der Kiwi-API.
 * E-Mail und Passwort werden ausschliesslich an Vorwerk gesendet und bleiben sonst lokal.
 *
 * Im Browser ist der Flow nicht umsetzbar: er braucht Cross-Origin-Anfragen und Zugriff auf
 * die Location-Header der Redirects, was die Same-Origin-Policy beides verhindert. Deshalb
 * meldet isSupported() dort false und das Feature bleibt ausgeblendet.
 */
@Injectable({
  providedIn: 'root'
})
export class ThermomixService {

  /** Identity-Provider von Vorwerk. */
  private static readonly CIAM_BASE = 'https://ciam.prod.cookidoo.vorwerk-digital.com';

  /** Hosts, die am Login beteiligt sind und deren Sitzungs-Cookies der Flow braucht. */
  private static readonly LOGIN_HOSTS = [
    'ciam.prod.cookidoo.vorwerk-digital.com',
    'eu.login.vorwerk.com'
  ];

  /** Oeffentliche Client-Kennung der Thermomix-App; ein Client-Secret braucht der PKCE-Flow nicht. */
  private static readonly CLIENT_ID = 'mobile-android';
  private static readonly REDIRECT_URI = 'com.vorwerk.cookidoo://code-grant';
  private static readonly SCOPE = 'openid profile email offline offline_access';

  /** Die Login-Seite liegt hinter Cloudflare und blockt Clients ohne browseraehnlichen User-Agent. */
  private static readonly BROWSER_USER_AGENT =
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/125.0.0.0 Safari/537.36';

  /** Das Access-Token kurz vor dem eigentlichen Ablauf erneuern. */
  private static readonly EXPIRY_MARGIN_MS = 5 * 60 * 1000;

  /** Obergrenze fuer die Redirect-Kette, damit eine Weiterleitungsschleife nicht haengen bleibt. */
  private static readonly MAX_REDIRECTS = 10;

  static readonly LOCALIZATIONS: ThermomixLocalization[] = [
    { Country: 'de', Language: 'de-DE', Url: 'https://cookidoo.de', Label: 'Deutschland' },
    { Country: 'at', Language: 'de-AT', Url: 'https://cookidoo.at', Label: 'Österreich' },
    { Country: 'ch', Language: 'de-CH', Url: 'https://cookidoo.ch', Label: 'Schweiz' }
  ];

  /** Cookies der laufenden Login-Sitzung, Name -> Wert. */
  private cookies: { [name: string]: string } = {};

  constructor(
    private platformService: PlatformService,
    private storageService: ThermomixStorageService
  ) { }

  /** Der Login-Flow laeuft nur in der App, nicht im Browser (siehe Klassenkommentar). */
  isSupported(): boolean {
    return this.platformService.isApp();
  }

  getLocalizations(): ThermomixLocalization[] {
    return ThermomixService.LOCALIZATIONS;
  }

  getLocalization(credentials: ThermomixCredentials): ThermomixLocalization {
    const country = credentials?.Country || 'de';
    return ThermomixService.LOCALIZATIONS.find(l => l.Country === country) || ThermomixService.LOCALIZATIONS[0];
  }

  /**
   * Prueft die Zugangsdaten mit einem echten Login und legt sie bei Erfolg lokal ab.
   * Gibt die Fehlermeldung zurueck oder null, wenn alles geklappt hat.
   */
  async connect(credentials: ThermomixCredentials): Promise<string> {
    if (!this.isSupported()) {
      return 'Die Thermomix-Verbindung ist nur in der App möglich.';
    }
    if (!credentials?.Email || !credentials?.Password) {
      return 'Bitte E-Mail-Adresse und Passwort angeben.';
    }
    try {
      const token = await this.login(credentials);
      await this.storageService.saveCredentials(credentials);
      await this.storageService.saveToken(token);
      return null;
    } catch (e) {
      return e?.message || 'Die Anmeldung bei Cookidoo ist fehlgeschlagen.';
    }
  }

  async disconnect() {
    this.cookies = {};
    return this.storageService.removeCredentials();
  }

  /**
   * Laedt die Einkaufsliste aus dem Cookidoo-Profil und bringt Rezeptzutaten sowie freie
   * Eintraege auf ein einheitliches Format. Eintraege, die in Cookidoo schon als vorhanden
   * markiert sind, bleiben aussen vor.
   */
  async getShoppingList(): Promise<ThermomixItem[]> {
    const credentials = await this.storageService.getCredentials();
    if (!credentials?.Email) {
      throw new Error('Es sind keine Thermomix-Zugangsdaten hinterlegt.');
    }
    const token = await this.getValidToken(credentials);
    const response = await this.requestShoppingList(credentials, token.AccessToken);
    if (response.status === 200) {
      return this.mapShoppingList(this.asJson(response.data));
    }
    // Ein abgelehntes Token ist der haeufigste Fehlerfall: einmal frisch anmelden und erneut
    // versuchen, bevor der Kunde mit einer Fehlermeldung behelligt wird.
    if (response.status === 401 || response.status === 403) {
      await this.storageService.removeToken();
      const freshToken = await this.login(credentials);
      await this.storageService.saveToken(freshToken);
      const retry = await this.requestShoppingList(credentials, freshToken.AccessToken);
      if (retry.status === 200) {
        return this.mapShoppingList(this.asJson(retry.data));
      }
      throw new Error('Cookidoo hat den Zugriff auf die Einkaufsliste verweigert.');
    }
    throw new Error('Die Einkaufsliste konnte nicht geladen werden.');
  }

  private async requestShoppingList(credentials: ThermomixCredentials, accessToken: string): Promise<HttpResponse> {
    const localization = this.getLocalization(credentials);
    return this.request({
      url: `${localization.Url}/shopping/${localization.Language}`,
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`
      }
    });
  }

  /** Flacht die Antwort der Einkaufsliste zu einer Liste von Eintraegen ab. */
  private mapShoppingList(list: ThermomixShoppingListJson): ThermomixItem[] {
    const items: ThermomixItem[] = [];
    const recipes: ThermomixShoppingRecipeJson[] = [...(list?.recipes || []), ...(list?.customerRecipes || [])];
    for (const recipe of recipes) {
      for (const ingredient of recipe.recipeIngredientGroups || []) {
        const item = this.mapIngredient(ingredient, recipe.title);
        if (item) {
          items.push(item);
        }
      }
    }
    for (const additionalItem of list?.additionalItems || []) {
      if (additionalItem.isOwned || !additionalItem.name) {
        continue;
      }
      items.push({ Id: additionalItem.id, Name: additionalItem.name, IsOwned: false });
    }
    return this.mergeItems(items);
  }

  private mapIngredient(ingredient: ThermomixIngredientJson, recipeTitle: string): ThermomixItem {
    if (ingredient?.isOwned || !ingredient?.ingredientNotation) {
      return null;
    }
    return {
      Id: ingredient.id,
      Name: ingredient.ingredientNotation,
      Amount: ingredient.quantity?.value,
      Unit: ingredient.unitNotation || '',
      RecipeTitle: recipeTitle,
      IsOwned: false
    };
  }

  /**
   * Fasst gleiche Zutaten aus verschiedenen Rezepten zusammen. Addiert wird nur bei gleicher
   * Einheit, sonst bleibt der erste Eintrag mit seiner Menge stehen und die Rezepte werden
   * zusammen genannt.
   */
  private mergeItems(items: ThermomixItem[]): ThermomixItem[] {
    const merged: ThermomixItem[] = [];
    for (const item of items) {
      const existing = merged.find(m => m.Name.toLowerCase() === item.Name.toLowerCase() && m.Unit === item.Unit);
      if (!existing) {
        merged.push({ ...item });
        continue;
      }
      if (existing.Amount != null && item.Amount != null) {
        existing.Amount = existing.Amount + item.Amount;
      }
      if (item.RecipeTitle && existing.RecipeTitle && existing.RecipeTitle.indexOf(item.RecipeTitle) === -1) {
        existing.RecipeTitle = `${existing.RecipeTitle}, ${item.RecipeTitle}`;
      }
    }
    return merged;
  }

  /** Liefert ein gueltiges Access-Token, erneuert es bei Bedarf per Refresh oder vollem Login. */
  private async getValidToken(credentials: ThermomixCredentials): Promise<ThermomixToken> {
    const stored = await this.storageService.getToken();
    if (stored?.AccessToken && stored.ExpiresAt && stored.ExpiresAt > Date.now()) {
      return stored;
    }
    if (stored?.RefreshToken) {
      const refreshed = await this.refresh(stored.RefreshToken);
      if (refreshed) {
        await this.storageService.saveToken(refreshed);
        return refreshed;
      }
    }
    const token = await this.login(credentials);
    await this.storageService.saveToken(token);
    return token;
  }

  private async login(credentials: ThermomixCredentials): Promise<ThermomixToken> {
    this.cookies = {};
    const localization = this.getLocalization(credentials);
    const verifier = this.createCodeVerifier();
    const pkce = await this.createCodeChallenge(verifier);
    const state = this.createRandomString(16);

    const requestId = await this.startAuthorization(localization, pkce, state);
    const code = await this.submitCredentials(requestId, credentials, state);
    return this.exchangeCode(code, verifier);
  }

  /**
   * Schritt 1: Der Authorize-Endpunkt leitet auf die Login-Seite um und haengt die requestId
   * an die Location an. Das HTML der Login-Seite muss dafuer nicht ausgewertet werden.
   */
  private async startAuthorization(localization: ThermomixLocalization, pkce: ThermomixPkce, state: string): Promise<string> {
    const response = await this.request({
      url: `${ThermomixService.CIAM_BASE}/authz-srv/authz`,
      method: 'GET',
      params: {
        response_type: 'code',
        client_id: ThermomixService.CLIENT_ID,
        redirect_uri: ThermomixService.REDIRECT_URI,
        market: localization.Country,
        scope: ThermomixService.SCOPE,
        state,
        code_challenge: pkce.Challenge,
        code_challenge_method: pkce.Method,
        ui_locales: localization.Language
      },
      headers: { 'User-Agent': ThermomixService.BROWSER_USER_AGENT },
      disableRedirects: true
    });
    const location = this.getLocation(response);
    const requestId = this.getQueryParam(location, 'requestId');
    if (!requestId) {
      throw new Error('Die Anmeldeseite von Cookidoo konnte nicht geöffnet werden.');
    }
    // Die Login-Seite einmal abrufen, damit die Sitzungs-Cookies gesetzt sind.
    await this.followRedirects(location, 'GET');
    return requestId;
  }

  /**
   * Schritt 2: Die Zugangsdaten an den Login-Service senden und der Redirect-Kette folgen,
   * bis die App-URL mit dem Authorization-Code erscheint.
   */
  private async submitCredentials(requestId: string, credentials: ThermomixCredentials, state: string): Promise<string> {
    const body = this.encodeForm({
      requestId,
      username: credentials.Email,
      password: credentials.Password
    });
    const response = await this.request({
      url: `${ThermomixService.CIAM_BASE}/login-srv/login`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'User-Agent': ThermomixService.BROWSER_USER_AGENT
      },
      data: body,
      disableRedirects: true
    });
    const location = await this.followRedirects(this.getLocation(response), 'GET');
    const error = this.getQueryParam(location, 'error');
    if (error) {
      throw new Error(this.getLoginErrorMessage(error));
    }
    const code = this.getQueryParam(location, 'code');
    if (!code) {
      throw new Error('Cookidoo hat keinen Anmelde-Code zurückgegeben.');
    }
    const returnedState = this.getQueryParam(location, 'state');
    if (returnedState && returnedState !== state) {
      throw new Error('Die Antwort von Cookidoo gehört nicht zu dieser Anmeldung.');
    }
    return code;
  }

  /** Schritt 3: Code gegen Tokens tauschen. Als oeffentlicher Client genuegt dafuer PKCE. */
  private async exchangeCode(code: string, verifier: string): Promise<ThermomixToken> {
    const response = await this.request({
      url: `${ThermomixService.CIAM_BASE}/token-srv/token`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json'
      },
      data: this.encodeForm({
        grant_type: 'authorization_code',
        code,
        redirect_uri: ThermomixService.REDIRECT_URI,
        client_id: ThermomixService.CLIENT_ID,
        code_verifier: verifier
      })
    });
    const token = this.toToken(response);
    if (!token) {
      throw new Error('Cookidoo hat kein gültiges Zugriffstoken ausgestellt.');
    }
    return token;
  }

  /** Erneuert das Access-Token. Gibt null zurueck, wenn das Refresh-Token nicht mehr gilt. */
  private async refresh(refreshToken: string): Promise<ThermomixToken> {
    try {
      const response = await this.request({
        url: `${ThermomixService.CIAM_BASE}/token-srv/token`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json'
        },
        data: this.encodeForm({
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
          client_id: ThermomixService.CLIENT_ID
        })
      });
      return this.toToken(response);
    } catch (e) {
      return null;
    }
  }

  private toToken(response: HttpResponse): ThermomixToken {
    if (response.status !== 200) {
      return null;
    }
    const data = this.asJson(response.data);
    if (!data?.access_token) {
      return null;
    }
    const expiresInMs = (Number(data.expires_in) || 0) * 1000;
    return {
      AccessToken: data.access_token,
      RefreshToken: data.refresh_token,
      ExpiresAt: Date.now() + Math.max(0, expiresInMs - ThermomixService.EXPIRY_MARGIN_MS)
    };
  }

  private getLoginErrorMessage(error: string): string {
    if (error === 'invalid_username_password') {
      return 'E-Mail-Adresse oder Passwort ist falsch.';
    }
    if (error === 'user_account_locked' || error === 'account_locked') {
      return 'Das Cookidoo-Konto ist gesperrt.';
    }
    return 'Die Anmeldung bei Cookidoo ist fehlgeschlagen.';
  }

  /**
   * Folgt einer Redirect-Kette und gibt die URL zurueck, bei der sie endet. Der Flow endet mit
   * einer Weiterleitung auf das App-Schema com.vorwerk.cookidoo://, die nicht mehr aufgerufen
   * werden kann und darf - der darin enthaltene Code ist genau das Ziel.
   */
  private async followRedirects(url: string, method: string): Promise<string> {
    let current = url;
    for (let i = 0; i < ThermomixService.MAX_REDIRECTS; i++) {
      if (!current) {
        return current;
      }
      if (current.indexOf(ThermomixService.REDIRECT_URI) === 0) {
        return current;
      }
      const response = await this.request({
        url: current,
        method,
        headers: { 'User-Agent': ThermomixService.BROWSER_USER_AGENT },
        disableRedirects: true
      });
      const location = this.getLocation(response);
      if (!location) {
        return current;
      }
      current = this.resolveUrl(current, location);
    }
    throw new Error('Die Anmeldung bei Cookidoo wurde zu oft weitergeleitet.');
  }

  /**
   * Fuehrt eine Anfrage aus und pflegt die Cookies der Login-Sitzung selbst, damit der Flow
   * nicht davon abhaengt, wie die Plattform Cookies zwischen den Anfragen behandelt.
   *
   * Die Cookies gehen nur an die Hosts des Login-Flows. Die Anfrage auf die Einkaufsliste
   * kommt mit dem Bearer-Token aus und soll die Sitzungs-Cookies nicht mitschleppen.
   */
  private async request(options: HttpOptions): Promise<HttpResponse> {
    const headers = { ...(options.headers || {}) };
    const isLoginHost = this.isLoginHost(options.url);
    const cookieHeader = this.getCookieHeader();
    if (isLoginHost && cookieHeader) {
      headers.Cookie = cookieHeader;
    }
    const response = await CapacitorHttp.request({ ...options, headers });
    if (isLoginHost) {
      this.storeCookies(response);
    }
    return response;
  }

  private isLoginHost(url: string): boolean {
    return ThermomixService.LOGIN_HOSTS.some(host => (url || '').indexOf(`https://${host}/`) === 0);
  }

  private getCookieHeader(): string {
    return Object.keys(this.cookies).map(name => `${name}=${this.cookies[name]}`).join('; ');
  }

  /**
   * Liest die Set-Cookie-Header aus. Mehrere Cookies koennen in einem Header zusammengefasst
   * sein; getrennt wird nur an Kommas, auf die wieder ein name=-Paar folgt, damit Datumswerte
   * wie "Expires=Sat, 04-Jan-25" nicht zerfallen.
   */
  private storeCookies(response: HttpResponse) {
    for (const key of Object.keys(response?.headers || {})) {
      if (key.toLowerCase() !== 'set-cookie') {
        continue;
      }
      const raw = String(response.headers[key] || '');
      for (const part of raw.split(/,(?=\s*[^\s;=,]+=)/)) {
        const pair = part.split(';')[0].trim();
        const separator = pair.indexOf('=');
        if (separator <= 0) {
          continue;
        }
        this.cookies[pair.substring(0, separator)] = pair.substring(separator + 1);
      }
    }
  }

  private getLocation(response: HttpResponse): string {
    for (const key of Object.keys(response?.headers || {})) {
      if (key.toLowerCase() === 'location') {
        return String(response.headers[key] || '');
      }
    }
    return null;
  }

  private resolveUrl(base: string, location: string): string {
    if (/^[a-z][a-z0-9+.-]*:/i.test(location)) {
      return location;
    }
    try {
      return new URL(location, base).toString();
    } catch (e) {
      return location;
    }
  }

  private getQueryParam(url: string, name: string): string {
    if (!url) {
      return null;
    }
    // Auch App-Schema-URLs wie com.vorwerk.cookidoo://code-grant?code=... muessen tragen,
    // deshalb wird der Query-Teil direkt ausgewertet statt ueber URL().
    const queryStart = url.indexOf('?');
    if (queryStart === -1) {
      return null;
    }
    const query = url.substring(queryStart + 1).split('#')[0];
    for (const pair of query.split('&')) {
      const separator = pair.indexOf('=');
      const key = separator === -1 ? pair : pair.substring(0, separator);
      if (decodeURIComponent(key) !== name) {
        continue;
      }
      return separator === -1 ? '' : decodeURIComponent(pair.substring(separator + 1).replace(/\+/g, ' '));
    }
    return null;
  }

  /**
   * Baut den Formular-Body selbst zusammen, statt CapacitorHttp ein Objekt zu uebergeben.
   * Beide Plattformen reichen einen String-Body unveraendert durch, und nur so ist das
   * Encoding verlaesslich: iOS wuerde ein Objekt ueber URLQueryItem serialisieren und dabei
   * ein '+' im Passwort stehen lassen, das der Server dann als Leerzeichen liest.
   */
  private encodeForm(values: { [key: string]: string }): string {
    return Object.keys(values)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(values[key])}`)
      .join('&');
  }

  private asJson(data: any): any {
    if (typeof data !== 'string') {
      return data;
    }
    try {
      return JSON.parse(data);
    } catch (e) {
      return null;
    }
  }

  private createCodeVerifier(): string {
    return this.createRandomString(32);
  }

  /**
   * Bildet das PKCE-Paar. crypto.subtle steht nur in einem Secure Context bereit, was je nach
   * WebView-Schema der App nicht garantiert ist; fehlt es, bleibt der von CIAM ebenfalls
   * angebotene Modus 'plain' als Rueckfallebene, damit die Anmeldung nicht komplett scheitert.
   */
  private async createCodeChallenge(verifier: string): Promise<ThermomixPkce> {
    try {
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
      return { Challenge: this.toBase64Url(new Uint8Array(digest)), Method: 'S256' };
    } catch (e) {
      return { Challenge: verifier, Method: 'plain' };
    }
  }

  private createRandomString(byteLength: number): string {
    const bytes = new Uint8Array(byteLength);
    crypto.getRandomValues(bytes);
    return this.toBase64Url(bytes);
  }

  private toBase64Url(bytes: Uint8Array): string {
    let binary = '';
    for (const byte of Array.from(bytes)) {
      binary += String.fromCharCode(byte);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
}
